import { useMutation } from 'react-query'
import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { SuggestionRequest } from 'types'

type BaseDesignEditOp<T extends string, P = {}> = {
  frame_index: number
  x: number
  y: number
  width: number
  height: number
  type: T
} & P

export interface UpdateTextOp
  extends BaseDesignEditOp<
    'update_text',
    {
      current_text: string
      new_text: string
    }
  > {}

export interface UpdateImageOp
  extends BaseDesignEditOp<
    'update_image',
    {
      prompt?: string
      image_urls?: string[]
    }
  > {}

export interface RemoveOp extends BaseDesignEditOp<'remove'> {}

export interface UpdateOp extends BaseDesignEditOp<'update', { prompt: string }> {}

export interface ReplaceLogoOp extends BaseDesignEditOp<'replace_logo', { image_url: string }> {}

export type DesignEditOp = UpdateTextOp | UpdateImageOp | RemoveOp | UpdateOp | ReplaceLogoOp

/** Maps `frame_index` (string key) → raster URL the user edited against; may differ from persisted design. */
export type FrameSourceUrls = Record<string, string>

interface ApplyDesignEditsParams {
  designKey: string
  operations: DesignEditOp[]
  frame_source_urls?: FrameSourceUrls
  /** Suggestion request id this edit builds on (e.g. variant from Edit more); omit when editing from original. */
  parent_request_id?: number
}

const applyDesignEdits = async (params: ApplyDesignEditsParams & { workspaceId: number }) => {
  const body: Record<string, unknown> = {
    operations: params.operations,
    auto_commit: false,
  }
  if (params.frame_source_urls && Object.keys(params.frame_source_urls).length > 0) {
    body.frame_source_urls = params.frame_source_urls
  }
  if (params.parent_request_id != null) {
    body.parent_request_id = params.parent_request_id
  }

  const res = await sendToApi<{ request: SuggestionRequest }>(
    `/w/${params.workspaceId}/designs/${params.designKey}/apply_edits`,
    {
      method: 'POST',
      body,
    },
  )

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error('Design edit request failed')
  }

  return res.data.request
}

export const useApplyDesignEdits = () => {
  const workspaceId = useCurrentWorkspaceId()
  return useMutation<SuggestionRequest, Error, ApplyDesignEditsParams>((params) =>
    applyDesignEdits({ workspaceId, ...params }),
  )
}
